// Réplica da função business_minutes() do Zoho Analytics (ver zoho-extraction.md):
// minutos úteis entre dois instantes, contando apenas janelas horárias diárias e
// ignorando sábados e domingos. O Zoho converte a data inicial para Europe/Lisbon
// (tz_date_created) e usa a data final em bruto — replicamos essa assimetria.

export type BusinessWindow = {
    startMinute: number;
    endMinute: number;
};

export type WallClock = {
    dayNumber: number;
    minuteOfDay: number;
    weekday: number;
};

const MS_PER_DAY = 24 * 60 * 60 * 1000;
const MAX_SPAN_DAYS = 120;

const wallClockFromParts = (year: number, month: number, day: number, hour: number, minute: number) => {
    const utcMidnight = Date.UTC(year, month - 1, day);
    const wall: WallClock = {
        dayNumber: Math.floor(utcMidnight / MS_PER_DAY),
        minuteOfDay: hour * 60 + minute,
        weekday: new Date(utcMidnight).getUTCDay(),
    };
    return wall;
};

export const utcWallClock = (isoTimestamp: string) => {
    const date = new Date(isoTimestamp);
    const wall = wallClockFromParts(date.getUTCFullYear(), date.getUTCMonth() + 1, date.getUTCDate(), date.getUTCHours(), date.getUTCMinutes());
    return wall;
};

const lisbonFormatter = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/Lisbon",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
});

export const lisbonWallClock = (isoTimestamp: string) => {
    const parts = lisbonFormatter.formatToParts(new Date(isoTimestamp));
    const partValue = (type: string) => Number(parts.find((part) => part.type === type)?.value || 0);
    const wall = wallClockFromParts(partValue("year"), partValue("month"), partValue("day"), partValue("hour") % 24, partValue("minute"));
    return wall;
};

// Partes da data em hora civil de Europe/Lisbon — usado para agrupar timestamptz (ex.: date_sent)
// por trimestre sem o desvio de fuso que um slice do ISO em UTC introduziria perto das fronteiras.
export const lisbonDateParts = (isoTimestamp: string) => {
    const parts = lisbonFormatter.formatToParts(new Date(isoTimestamp));
    const partValue = (type: string) => Number(parts.find((part) => part.type === type)?.value || 0);
    const dateParts = { year: partValue("year"), month: partValue("month"), day: partValue("day") };
    return dateParts;
};

const isWeekend = (weekday: number) => weekday === 0 || weekday === 6;

export const businessMinutesBetween = (start: WallClock, end: WallClock, windows: Array<BusinessWindow>) => {
    if (end.dayNumber < start.dayNumber || (end.dayNumber === start.dayNumber && end.minuteOfDay <= start.minuteOfDay)) {
        return 0;
    }

    let totalMinutes = 0;
    const lastDay = Math.min(end.dayNumber, start.dayNumber + MAX_SPAN_DAYS);
    for (let dayNumber = start.dayNumber; dayNumber <= lastDay; dayNumber++) {
        const weekday = (new Date(dayNumber * MS_PER_DAY).getUTCDay() + 7) % 7;
        if (isWeekend(weekday)) continue;

        const dayStartMinute = dayNumber === start.dayNumber ? start.minuteOfDay : 0;
        const dayEndMinute = dayNumber === end.dayNumber ? end.minuteOfDay : 24 * 60;

        for (const window of windows) {
            const overlapStart = Math.max(window.startMinute, dayStartMinute);
            const overlapEnd = Math.min(window.endMinute, dayEndMinute);
            if (overlapEnd > overlapStart) totalMinutes += overlapEnd - overlapStart;
        }
    }
    return totalMinutes;
};
